/** @file planBulkUpload: matches uploaded files to slots by name: fill now, needs a fit, skipped. */
import type { ImageSlot } from '@zilo/pose3d-format/types/facet';
import type { BulkPlan, MisfitImage, SkippedFile, SlotImage, UploadFile } from './artTypes';
import { fitChoice } from './fitChoice';
import { readPngSize } from './readPngSize';
import { slotKeyOfFile } from './slotKeyOfFile';

type Matched = { ok: SlotImage; size: [number, number] } | SkippedFile;

/** Reads one file's slot and PNG size; the reason it cannot be used otherwise. */
function match(file: UploadFile, slots: ReadonlyMap<string, ImageSlot>): Matched {
  const key = slotKeyOfFile(file.name, new Set(slots.keys()));
  if (key === null) return { name: file.name, reason: /\.png$/i.test(file.name) ? 'no slot has this name' : 'not a PNG or zip' };
  try {
    return { ok: { name: file.name, slot: key, png: file.bytes }, size: readPngSize(file.bytes) };
  } catch {
    return { name: file.name, reason: 'not a readable PNG' };
  }
}

/**
 * Every file against the slots (`<drawable>.<entry>.<tile>.png`): unknown names, unreadable files and a second file for
 * a slot are `skipped` with a reason (the later file of two for one slot wins); a size other than the slot's is a
 * `misfit`, waiting for the user's Pad / Scale choice; the rest is `ready`.
 */
export function planBulkUpload(files: readonly UploadFile[], slots: readonly ImageSlot[]): BulkPlan {
  const bySlot = new Map(slots.map((s) => [s.key, s]));
  const claimed = new Map<string, { image: SlotImage; size: [number, number] }>();
  const skipped: SkippedFile[] = [];
  for (const file of files) {
    const m = match(file, bySlot);
    if ('reason' in m) { skipped.push(m); continue; }
    const earlier = claimed.get(m.ok.slot);
    if (earlier !== undefined) skipped.push({ name: earlier.image.name, reason: `${m.ok.name} fills the same slot` });
    claimed.set(m.ok.slot, { image: m.ok, size: m.size });
  }
  const ready: SlotImage[] = [];
  const misfits: MisfitImage[] = [];
  for (const { image, size } of claimed.values()) {
    const slotSize = bySlot.get(image.slot)?.size ?? size;
    if (fitChoice(size, slotSize) === 'exact') ready.push(image);
    else misfits.push({ ...image, size, slotSize });
  }
  return { ready, misfits, skipped };
}
